import {
    Alert,
    Button,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
} from "react-bootstrap"

import type {
    FreeOutletEditor,
    FreeOutletType,
    NewSeat,
    Seat,
    SeatPlan,
} from "./types"

export const RoomSeatCreationModal = ({
    value,
    seatPlan,
    saving,
    error,
    onChange,
    onClose,
    onSave,
}: {
    value: NewSeat | null
    seatPlan: SeatPlan | null
    saving: boolean
    error: string | null
    onChange: (value: NewSeat) => void
    onClose: () => void
    onSave: () => void
}) => <Modal show={Boolean(value)}
    onHide={() => !saving && onClose()} centered>
    <Modal.Header closeButton>
        <Modal.Title>Sitzplatz hinzufügen</Modal.Title>
    </Modal.Header>
    {value && <>
        <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="info">
                Block, Reihe und Platz wurden aus der angeklickten Position
                vorgeschlagen. Bitte vor dem Speichern prüfen.
            </Alert>
            <Form.Label htmlFor="new-seat-section">Block</Form.Label>
            <Form.Select id="new-seat-section" className="mb-3"
                value={value.section}
                onChange={event => onChange({
                    ...value,
                    section: event.target.value,
                })}>
                {seatPlan?.sections.map(section =>
                    <option key={section.key} value={section.key}>
                        {section.name} ({section.key})
                    </option>)}
            </Form.Select>
            <Row className="g-3 mb-3">
                <Col>
                    <Form.Label htmlFor="new-seat-row">Reihe</Form.Label>
                    <Form.Control id="new-seat-row" type="number" min={1}
                        step={1} value={value.row}
                        onChange={event => onChange({
                            ...value,
                            row: Number(event.target.value),
                        })}/>
                </Col>
                <Col>
                    <Form.Label htmlFor="new-seat-column">Platz</Form.Label>
                    <Form.Control id="new-seat-column" type="number" min={1}
                        step={1} value={value.column}
                        onChange={event => onChange({
                            ...value,
                            column: Number(event.target.value),
                        })}/>
                </Col>
            </Row>
            <Form.Check type="switch" className="mb-3"
                id="new-seat-writing-surface"
                label="Schreibfläche vorhanden"
                checked={value.hasWritingSurface}
                onChange={event => onChange({
                    ...value,
                    hasWritingSurface: event.target.checked,
                })}/>
            <Form.Check type="switch"
                id="new-seat-accessible"
                label="Barrierefreier Sitzplatz"
                checked={value.accessible}
                onChange={event => onChange({
                    ...value,
                    accessible: event.target.checked,
                })}/>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
                Abbrechen
            </Button>
            <Button onClick={onSave} disabled={saving ||
                !Number.isInteger(value.row) || value.row < 1 ||
                !Number.isInteger(value.column) || value.column < 1}>
                {saving && <Spinner size="sm" className="me-2"/>}
                Sitz hinzufügen
            </Button>
        </Modal.Footer>
    </>}
</Modal>

export const RoomOutletEditorModal = ({
    value,
    saving,
    error,
    onChange,
    onClose,
    onSave,
    onDelete,
}: {
    value: FreeOutletEditor | null
    saving: boolean
    error: string | null
    onChange: (value: FreeOutletEditor) => void
    onClose: () => void
    onSave: () => void
    onDelete: () => void
}) => <Modal show={Boolean(value)}
    onHide={() => !saving && onClose()} centered>
    <Modal.Header closeButton>
        <Modal.Title>Freie Steckdose bearbeiten</Modal.Title>
    </Modal.Header>
    {value && <>
        <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Label htmlFor="free-outlet-type">Typ</Form.Label>
            <Form.Select id="free-outlet-type" className="mb-3"
                value={value.type}
                onChange={event => onChange({
                    ...value,
                    type: event.target.value as FreeOutletType,
                })}>
                <option value="WALL">Wandsteckdose</option>
                <option value="FLOOR">Bodendose</option>
                <option value="LECTERN">Pultanschluss</option>
            </Form.Select>
            <Form.Label htmlFor="free-outlet-label">Bezeichnung</Form.Label>
            <Form.Control id="free-outlet-label" className="mb-3"
                placeholder="z. B. Mittelgang Reihe 8" value={value.label}
                onChange={event => onChange({
                    ...value,
                    label: event.target.value,
                })}/>
            <Row className="g-3 mb-3">
                <Col xs={4}>
                    <Form.Label htmlFor="free-outlet-count">Anzahl Dosen</Form.Label>
                    <Form.Control id="free-outlet-count" type="number"
                        min={1} max={20} value={value.socketCount}
                        onChange={event => onChange({
                            ...value,
                            socketCount: Number(event.target.value),
                        })}/>
                </Col>
                <Col>
                    <Form.Label htmlFor="free-outlet-fuse">
                        Fuse-/Sicherungsbeschriftung
                    </Form.Label>
                    <Form.Control id="free-outlet-fuse"
                        placeholder="z. B. UV-1 · F12" value={value.fuseLabel}
                        onChange={event => onChange({
                            ...value,
                            fuseLabel: event.target.value,
                        })}/>
                </Col>
            </Row>
            <Form.Label htmlFor="free-outlet-note">Notiz</Form.Label>
            <Form.Control id="free-outlet-note" as="textarea" rows={3}
                value={value.note}
                onChange={event => onChange({
                    ...value,
                    note: event.target.value,
                })}/>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
            <div>
                {value.key && <Button variant="outline-danger"
                    onClick={onDelete} disabled={saving}>Entfernen</Button>}
            </div>
            <div className="d-flex gap-2">
                <Button variant="secondary" onClick={onClose}
                    disabled={saving}>Abbrechen</Button>
                <Button onClick={onSave} disabled={saving}>
                    {saving && <Spinner size="sm" className="me-2"/>}
                    Speichern
                </Button>
            </div>
        </Modal.Footer>
    </>}
</Modal>

export const RoomSeatEditorModal = ({
    seat,
    saving,
    error,
    outletPresent,
    socketCount,
    fuseLabel,
    onSeatChange,
    onOutletPresentChange,
    onSocketCountChange,
    onFuseLabelChange,
    onClose,
    onSave,
}: {
    seat: Seat | null
    saving: boolean
    error: string | null
    outletPresent: boolean
    socketCount: number
    fuseLabel: string
    onSeatChange: (seat: Seat) => void
    onOutletPresentChange: (present: boolean) => void
    onSocketCountChange: (count: number) => void
    onFuseLabelChange: (label: string) => void
    onClose: () => void
    onSave: () => void
}) => <Modal show={Boolean(seat)}
    onHide={() => !saving && onClose()} centered>
    <Modal.Header closeButton>
        <Modal.Title>Sitzplatz bearbeiten</Modal.Title>
    </Modal.Header>
    {seat && <>
        <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <dl className="row small mb-3">
                <dt className="col-4">Sitzkennung</dt>
                <dd className="col-8">{seat.key}</dd>
                <dt className="col-4">Rasterposition</dt>
                <dd className="col-8">
                    Bereich {seat.section}, Reihe {seat.row}, Platz {seat.column}
                </dd>
            </dl>
            <Form.Check type="switch" className="mb-3"
                id="seat-writing-surface" label="Schreibfläche vorhanden"
                checked={seat.hasWritingSurface}
                onChange={event => onSeatChange({
                    ...seat,
                    hasWritingSurface: event.target.checked,
                })}/>
            <Form.Check type="switch" className="mb-3"
                id="seat-accessible" label="Barrierefreier Sitzplatz"
                checked={Boolean(seat.accessible)}
                onChange={event => onSeatChange({
                    ...seat,
                    accessible: event.target.checked,
                })}/>
            <hr/>
            <Form.Check type="switch" className="mb-3"
                id="seat-fixed-outlet"
                label="Feste Steckdose an diesem Sitzplatz"
                checked={outletPresent}
                onChange={event =>
                    onOutletPresentChange(event.target.checked)}/>
            {outletPresent && <Row className="g-3 mb-3">
                <Col xs={4}>
                    <Form.Label htmlFor="seat-socket-count">Anzahl Dosen</Form.Label>
                    <Form.Control id="seat-socket-count" type="number"
                        min={1} max={20} step={1} value={socketCount}
                        onChange={event =>
                            onSocketCountChange(Number(event.target.value))}/>
                </Col>
                <Col>
                    <Form.Label htmlFor="seat-fuse-label">
                        Fuse-/Sicherungsbeschriftung
                    </Form.Label>
                    <Form.Control id="seat-fuse-label" type="text"
                        maxLength={100} placeholder="z. B. UV-1 · F12"
                        value={fuseLabel}
                        onChange={event => onFuseLabelChange(event.target.value)}/>
                    <Form.Text>
                        Leer lassen, wenn die Zuordnung noch unbekannt ist.
                    </Form.Text>
                </Col>
            </Row>}
            <hr/>
            <Row className="g-3">
                {(["x", "y"] as const).map(axis => <Col key={axis}>
                    <Form.Label htmlFor={`seat-position-${axis}`}>
                        {axis === "x"
                            ? "Horizontale Planposition"
                            : "Vertikale Planposition"}
                    </Form.Label>
                    <Form.Control id={`seat-position-${axis}`} type="number"
                        min={0} max={1} step={0.001}
                        value={seat.position[axis]}
                        onChange={event => onSeatChange({
                            ...seat,
                            position: {
                                ...seat.position,
                                [axis]: Number(event.target.value),
                            },
                        })}/>
                </Col>)}
            </Row>
            <Form.Text>
                Planpositionen liegen zwischen 0 und 1. Sitzkennung, Bereich,
                Reihe und Platz bleiben unverändert, damit Nachbarschaften
                stabil bleiben.
            </Form.Text>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
                Abbrechen
            </Button>
            <Button onClick={onSave} disabled={saving}>
                {saving && <Spinner size="sm" className="me-2"/>}
                Speichern
            </Button>
        </Modal.Footer>
    </>}
</Modal>
